import React from "react";
import * as ReactBootstrap from "react-bootstrap";

import {Confirm, ICONS} from "../../../components";
import type {BuybackEMIRecord} from "./types";

/**
 * One node in the Q&A tree: a question, its answer, and optional children
 * for the next question down the decision branch.
 */
interface QANode {
  /** Stable key — used for React keys. */
  key: string;
  question: string;
  answer: string;
  children?: QANode[];
}

interface BuybackEMISummaryProps {
  record: BuybackEMIRecord;
  isDeleting: boolean;
  onDelete: () => void;
}

/** Map a record into the question-and-answer tree the UI renders. */
function buildQATree(record: BuybackEMIRecord): QANode {
  const eligibilityLabel =
    record.eligibility_yes_no_label || record.eligibility_yes_no || "—";

  const root: QANode = {
    key: "q1",
    question:
      "Was the customer eligible for No Cost EMI / Buyback (vs Hardware Service options)?",
    answer: eligibilityLabel,
    children: [],
  };

  if (record.eligibility_yes_no !== "yes") return root;

  const pathLabel = record.customer_path_label || record.customer_path || "—";
  const pathNode: QANode = {
    key: "q2",
    question: "Which path did the customer choose?",
    answer: pathLabel,
    children: [],
  };
  root.children!.push(pathNode);

  if (record.customer_path !== "emi-buyback") return root;

  const emiOrBuybackLabel =
    record.emi_or_buyback_label || record.emi_or_buyback || "—";
  const emiNode: QANode = {
    key: "q3",
    question:
      "Did customer agree for No Cost EMI (vs full payment / Buyback)?",
    answer: emiOrBuybackLabel,
    children: [],
  };
  pathNode.children!.push(emiNode);

  if (record.emi_or_buyback !== "buyback") return root;

  const buybackOptedLabel =
    record.buyback_opted_label || record.buyback_opted || "—";
  const optedNode: QANode = {
    key: "q4",
    question: "Did the customer opt for Buyback?",
    answer: buybackOptedLabel,
    children: [],
  };
  emiNode.children!.push(optedNode);

  const quotationIdAnswer =
    record.quotation_id != null && record.quotation_id !== ""
      ? record.quotation_id
      : "—";

  if (record.buyback_opted === "yes") {
    optedNode.children!.push({
      key: "q5-amount",
      question: "Quotation Amount",
      answer:
        record.quotation_amount != null && record.quotation_amount !== ""
          ? `₹${record.quotation_amount}`
          : "—",
    });
    optedNode.children!.push({
      key: "q5-quotation-id",
      question: "Quotation ID",
      answer: quotationIdAnswer,
    });
  } else if (record.buyback_opted === "no") {
    const reasonLabel =
      record.rejection_reason_label || record.rejection_reason || "—";
    optedNode.children!.push({
      key: "q5-reason",
      question: "Reason for not opting for Buyback",
      answer: reasonLabel,
    });
    optedNode.children!.push({
      key: "q5-amount",
      question: "Quotation Amount",
      answer:
        record.quotation_amount != null && record.quotation_amount !== ""
          ? `₹${record.quotation_amount}`
          : "—",
    });
    optedNode.children!.push({
      key: "q5-quotation-id",
      question: "Quotation ID",
      answer: quotationIdAnswer,
    });
  }

  return root;
}

/**
 * Recursive renderer: each node is a card-styled row inside an indented
 * ``ListGroup``; children get a left border + extra padding so the
 * conditional flow visually reads as a tree.
 */
function QATreeNode({node}: {node: QANode}): JSX.Element {
  const hasChildren = (node.children?.length ?? 0) > 0;
  return (
    <ReactBootstrap.ListGroup.Item className="px-3 py-2 border-0 bg-transparent">
      <div className="border rounded p-2 bg-light">
        <div className="text-muted small text-uppercase fw-semibold">
          {node.question}
        </div>
        <div className="fw-semibold">{node.answer}</div>
      </div>
      {hasChildren ? (
        <ReactBootstrap.ListGroup
          variant="flush"
          className="border-start ms-3 mt-2 ps-2"
        >
          {node.children!.map((child) => (
            <QATreeNode key={child.key} node={child} />
          ))}
        </ReactBootstrap.ListGroup>
      ) : null}
    </ReactBootstrap.ListGroup.Item>
  );
}

function formatCreatedBy(record: BuybackEMIRecord): string | null {
  const who = record.created_by?.trim();
  const when = record.updated_at || record.created_at;
  if (!who && !when) return null;
  const whenStr = when ? new Date(when).toLocaleString() : "";
  if (who && whenStr) return `Last updated by ${who} on ${whenStr}`;
  if (who) return `Captured by ${who}`;
  return whenStr ? `Last updated on ${whenStr}` : null;
}

/**
 * Read-only summary card rendering the Buyback / EMI capture as a Q&A tree.
 *
 * Once a record exists for a ticket the capture is treated as immutable: the
 * only mutating action exposed is Delete (gated by a destructive confirmation
 * dialog). To "edit" a record the user must delete and re-capture, which
 * preserves a clean change-log trail.
 */
export const BuybackEMISummary: React.FC<BuybackEMISummaryProps> = ({
  record,
  isDeleting,
  onDelete,
}) => {
  const root = buildQATree(record);
  const meta = formatCreatedBy(record);

  return (
    <ReactBootstrap.Card>
      <ReactBootstrap.Card.Header className="bg-dark text-white">
        <ReactBootstrap.Row className="align-items-center g-2">
          <ReactBootstrap.Col xs="12" md="8">
            <div className="fw-semibold">Buyback / EMI Capture</div>
            {meta ? (
              <div className="small text-white-50">{meta}</div>
            ) : null}
          </ReactBootstrap.Col>
          <ReactBootstrap.Col xs="12" md="4" className="text-md-end">
            <Confirm
              title="Delete Buyback / EMI record?"
              body="This will remove the captured decision tree for this ticket. You can re-capture it after deletion."
              confirmText={isDeleting ? "Deleting…" : "Delete"}
              confirmBSStyle="danger"
              onConfirm={onDelete}
            >
              <ReactBootstrap.Button
                variant="danger"
                size="sm"
                disabled={isDeleting}
                title="Delete"
                aria-label="Delete Buyback / EMI record"
              >
                {ICONS.DELETE} Delete
              </ReactBootstrap.Button>
            </Confirm>
          </ReactBootstrap.Col>
        </ReactBootstrap.Row>
      </ReactBootstrap.Card.Header>
      <ReactBootstrap.Card.Body className="p-2">
        <ReactBootstrap.ListGroup variant="flush">
          <QATreeNode node={root} />
        </ReactBootstrap.ListGroup>
        {record.notes && record.notes.trim() !== "" ? (
          <div className="border rounded p-2 bg-light mx-3 mb-2">
            <div className="text-muted small text-uppercase fw-semibold">
              Remarks
            </div>
            <div className="fw-semibold" style={{whiteSpace: "pre-wrap"}}>
              {record.notes}
            </div>
          </div>
        ) : null}
      </ReactBootstrap.Card.Body>
    </ReactBootstrap.Card>
  );
};

export default BuybackEMISummary;
